import React, { useState, useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ScrollReveal from "scrollreveal";
import styles from "../services/Services.module.css";

import img1 from "../../images/לילך מורד גן יבנה 12.webp";
import img2 from "../../images/לילך מורד גן יבנה 35.webp";
import img3 from "../../images/לילך מורד גן יבנה 65.webp";
import img4 from "../../images/לילך מורד גן יבנה 95.webp";
import hero3 from "../../images/לילך מורד גן יבנה 70.webp";

gsap.registerPlugin(ScrollTrigger);

const WHATSAPP_NUMBER = "972505864422";
const waUrl = (t) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`היי לילך, הגעתי מהאתר שלך ואשמח לשמוע עוד על ${t}`)}`;

const RotatingText = ({ texts, interval = 3200 }) => {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx((prev) => (prev + 1) % texts.length);
        setVisible(true);
      }, 350);
    }, interval);
    return () => clearInterval(timer);
  }, [texts.length, interval]);

  return (
    <span style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(-8px)",
      transition: "opacity 0.5s ease, transform 0.5s ease",
      display: "inline-block",
    }}>
      {texts[idx]}
    </span>
  );
};

const TargetSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(`.${styles.targetTop}`,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 1, ease: "power3.out",
          scrollTrigger: { trigger: `.${styles.targetTop}`, start: "top 85%" } }
      );

      gsap.fromTo(`.${styles.targetEmpathy}`,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 1, ease: "power3.out",
          scrollTrigger: { trigger: `.${styles.targetEmpathy}`, start: "top 85%" } }
      );

      gsap.fromTo(`.${styles.targetTransition}`,
        { opacity: 0, y: 25 },
        { opacity: 1, y: 0, duration: 0.9, ease: "power3.out",
          scrollTrigger: { trigger: `.${styles.targetTransition}`, start: "top 80%" } }
      );

    }, sectionRef);
    return () => ctx.revert();
  }, []);

  // Image clipPath reveal from the side
  useEffect(() => {
    if (!sectionRef.current) return;
    const rows = sectionRef.current.querySelectorAll('[data-adv-row]');
    const triggers = [];
    rows.forEach((row, i) => {
      const imgEl = row.querySelector('[data-reveal-img] img');
      if (!imgEl) return;
      const isReversed = i % 2 === 1;
      // RTL: normal = image left → reveal from left, reversed = image right → reveal from right
      // clipPath polygon reveal from the side the image sits on
      // Normal (image left): wipe from left to right
      // Reversed (image right): wipe from right to left
      const fromPoly = isReversed
        ? "polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)"
        : "polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)";
      const toPoly = "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)";
      gsap.set(imgEl, { clipPath: fromPoly });
      const st = ScrollTrigger.create({
        trigger: row,
        start: "top 78%",
        onEnter: () => {
          gsap.to(imgEl, { clipPath: toPoly, duration: 1.4, ease: "power4.inOut" });
        },
        once: true,
      });
      triggers.push(st);
    });
    return () => triggers.forEach((st) => st.kill());
  }, []);

  // ScrollReveal for text content
  useEffect(() => {
    if (!sectionRef.current) return;
    const sr = ScrollReveal({
      reset: false,
      distance: "25px",
      duration: 900,
      easing: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
      viewFactor: 0.1,
    });
    const el = sectionRef.current;

    // Empathy text and accent
    const empathy = el.querySelector('[data-sr="empathy"]');
    if (empathy) sr.reveal(empathy, { origin: "top", delay: 100 });
    const empathyAccent = el.querySelector('[data-sr="empathy-accent"]');
    if (empathyAccent) sr.reveal(empathyAccent, { origin: "top", delay: 200 });

    // Decision tags
    const tags = el.querySelectorAll('[data-sr="tag"]');
    tags.forEach((tag, i) => {
      sr.reveal(tag, { origin: "top", delay: 250 + i * 120 });
    });

    // Transition titles and text
    const transTitle = el.querySelector('[data-sr="trans-title"]');
    if (transTitle) sr.reveal(transTitle, { origin: "top", delay: 100 });
    const transSub = el.querySelector('[data-sr="trans-sub"]');
    if (transSub) sr.reveal(transSub, { origin: "top", delay: 200 });
    const transEnsure = el.querySelector('[data-sr="trans-ensure"]');
    if (transEnsure) sr.reveal(transEnsure, { origin: "top", delay: 300 });

    // Bottom section
    const bottom = el.querySelector('[data-sr="bottom"]');
    if (bottom) sr.reveal(bottom, { origin: "top", delay: 100 });

    // Advantage rows
    const rows = el.querySelectorAll('[data-adv-row]');
    rows.forEach((row, i) => {
      const content = row.querySelector('[data-reveal-text]');
      if (!content) return;
      const isReversed = i % 2 === 1;
      sr.reveal(content, {
        origin: isReversed ? "left" : "right",
        distance: "40px",
        delay: 150,
      });
    });
    return () => sr.destroy();
  }, []);

  return (
    <div className={styles.servicesSection} ref={sectionRef}>
      <div className={styles.targetSection}>
        <div className={styles.targetInner}>

          {/* Rotating Questions */}
          <div className={styles.targetTop}>
            <RotatingText texts={[
              "קניתם דירה מקבלן ולא יודעים מאיפה להתחיל?",
              "מתכננים בנייה מאפס ומרגישים שזה גדול עליכם?",
              "רוצים לשפץ אבל מפחדים מחריגה בתקציב?",
              "קניתם דירה חדשה מקבלן?",
            ]} />
          </div>

          {/* Empathy */}
          <div className={styles.targetEmpathy}>
            <p className={styles.targetEmpathyText} data-sr="empathy">
              קודם כל, מזל טוב! בין אם אתם בונים, משפצים או נכנסים לדירה חדשה - זה רגע מרגש. אני בטוחה שכבר התחלתם לדמיין איך הסלון ייראה, איזה מטבח תרצו, ואיך הילדים רצים במסדרון.
            </p>
            <p className={styles.targetEmpathyAccent} data-sr="empathy-accent">
              אבל לאט לאט התחלתם לגלות שבדרך יש גם..
            </p>
            <div className={styles.decisionTags}>
              {["עשרות החלטות שצריך לקבל", "לוחות זמנים שלוחצים", "יותר מדי דעות מבעלי מקצוע שמתחילות לבלבל", "השראות מהרשת שלא תמיד מתאימות למציאות"].map((tag, i) => (
                <span key={i} className={styles.decisionTag} data-sr="tag">
                  <span className={styles.decisionTagShine} />
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Transition */}
          <div className={styles.targetTransition}>
            <h3 className={styles.targetTransTitle} data-sr="trans-title">
              אבל למזלכם, אתם לא לבד
            </h3>
            <p className={styles.targetTransSub} data-sr="trans-sub">
              אחרי שליוויתי עשרות משפחות מהרגע שהכל היה רק חלום ועד הרגע שכל פינה בבית הרגישה נכון - אני יודעת בדיוק איך להפוך את התהליך הזה לפשוט, ברור ובלי הפתעות.
            </p>
            <h3 className={styles.targetTransTitle} style={{ marginTop: "32px" }} data-sr="trans-ensure">
              ואני כאן לוודא ש...
            </h3>
          </div>

          {/* Advantages Rows */}
          <div className={styles.advRows}>
            {[
              { title: "כל שקל שלכם הולך למקום הנכון", desc: "בלי תכנון נכון, משפחות מפסידות עשרות אלפי שקלים על שינויים באמצע העבודה, חומרים שלא מתאימים וקבלנים שחוזרים לתקן. עם תכנון מדויק מראש - כל ההחלטות סגורות לפני שנוגעים בקיר הראשון, והכסף הולך רק למה שבאמת צריך.", img: img1 },
              { title: "הכל מתנהל חלק מהתחלה ועד הסוף", desc: "בלי ניהול צמוד, לוחות זמנים נשברים, בעלי מקצוע לא מתואמים ביניהם, ואתם נמצאים באמצע - מנסים לרכז הכל לבד. עם ליווי מקצועי - כל בעל מקצוע מקבל תוכנית מפורטת, הפיקוח בשטח עליי, והדבר היחיד שאתם צריכים לעשות הוא להיכנס לבית בראש שקט.", img: img2 },
              { title: "אין הפתעות בתקציב", desc: "בלי תכנון ותקצוב מסודר מראש, הפרויקט תמיד עולה יותר ממה שתכננתם - תוספות שלא חשבתם עליהן, הצעות מחיר שלא ברור מה הן כוללות, והרגשה שהתקציב ברח מהידיים. עם תכנון תקציבי מדויק - תדעו מראש בדיוק כמה זה עולה, איפה שווה להשקיע ועל מה אפשר לחסוך בלי להתפשר.", img: img3 },
              { title: "תראו הכל לפני שמתחילים", desc: "תקבלו הדמיות ריאליסטיות שלא משאירות מקום לדמיון - צבעים, חומרים, תאורה, ריהוט, הכל מול העיניים בדיוק כמו שייראה במציאות. ואם משהו לא מרגיש נכון - משנים על המסך, לא על הקירות.", img: img4 },
              { title: "הבית נבנה סביב החיים שלכם", desc: "בית שנראה יפה בתמונה זה לא מספיק - הוא צריך להתאים למי שגר בו. אני מתכננת כל חלל סביב השגרה האמיתית שלכם - איך אתם מבשלים, איפה הילדים עושים שיעורים, מה קורה כשכולם בבית ביחד. התוצאה היא בית שלא רק יפה, אלא פשוט עובד.", img: hero3 },
            ].map((adv, i) => (
              <div key={i} data-adv-row className={`${styles.advRow} ${i % 2 === 1 ? styles.advRowReverse : ""}`}>
                <div className={styles.advRowContent} data-reveal-text>
                  <h4 className={styles.advRowTitle}>{adv.title}</h4>
                  <p className={styles.advRowDesc}>{adv.desc}</p>
                </div>
                <div className={styles.advRowImage} data-reveal-img>
                  <img src={adv.img} alt={adv.title} loading="lazy" />
                </div>
              </div>
            ))}
          </div>

          <div className={styles.targetBottom} data-sr="bottom">
            <p className={styles.targetBottomText}>השקעה בבית או בבנייה שלו תהיה בין ההשקעות הגדולות בחיים שלכם.<br />חשוב שתיכנסו הביתה ותרגישו שכל פינה נבנתה בשבילכם, שכל מטר בבית נוצל - ותחיו בבית שמשרת אתכם, לא אתם את הבית.</p>
            <a href={waUrl("ייעוץ ראשוני")} target="_blank" rel="noopener noreferrer" className={styles.targetCta}>
              <span>לשיחת ייעוץ ללא עלות לחצו כאן</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TargetSection;
